// /events (D43/D44): every event in one sortable, filterable, paged table. Calculated interest is
// booked hourly and is visible by default (D44); the owner filters it out with the Forrás chips or
// the Típus list. The filter lives in the URL (bookmarkable) and in localStorage.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDeals, getEventsPage, getLocationItems, patchAnnotation, postVoidEvent } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { EventRow, EventSortKey, EventSource, EventType, EventsPage } from '../api/model';
import { isUnassignedRepay } from '../api/adapt';
import { ErrorNotice, Loading, Notice } from '../components/Async';
import { AssignDealDialog, BulkAssignDialog, canAssignDeal } from '../components/AssignDealDialog';
import { CardHead } from '../components/Card';
import { Dialog } from '../components/Dialog';
import { PriceCell, SourcePills } from '../components/EventsTable';
import { HelpLabel } from '../components/Help';
import { Table, type Column } from '../components/Table';
import { Dash, signClass } from '../components/Val';
import { compareDecimal, formatDecimal, isDecimalString, subDecimal } from '../lib/decimal';
import {
  ALL_SOURCES,
  ALL_TYPES,
  PAGE_SIZES,
  clearFilters,
  defaultCustomRange,
  filterToParams,
  filterToQuery,
  initialFilter,
  isDefaultFilter,
  paramsToFilter,
  storeFilter,
  type EventsFilter,
  type Period,
  type PageSize,
} from '../lib/eventsFilter';
import { eventTypeText, locationText, TYPE_LABEL } from '../lib/labels';
import { Link, useRouter } from '../lib/router';
import { formatUtc } from '../lib/time';

const SOURCE_CHIP: Record<EventSource, string> = { manual: 'Kézi', binance: 'Binance', calculated: 'Számolt kamat' };
const PERIOD_LABEL: Record<Period, string> = { today: 'Ma', '7d': '7 nap', '30d': '30 nap', all: 'Mind', custom: 'Egyéni' };
const EMPTY_PAGE: EventsPage = { items: [], total: 0, limit: 0, offset: 0, facets: null };

const toggle = <T,>(list: readonly T[], all: readonly T[], x: T): T[] => all.filter((a) => (a === x ? !list.includes(a) : list.includes(a)));

function quantityCell(e: EventRow) {
  const text = formatDecimal(e.display_quantity, { sign: e.direction === 'in' || e.type === 'result' });
  return (
    <span className="mono">
      {text} <b>{e.asset}</b>
    </span>
  );
}

const resultClass = (v: string): string => (signClass(v) === 'neg' ? 'warn' : signClass(v));

/** The type, the "Hozzárendelésre vár" badge (D47) and a repay's split / link summary (D46, D48). */
function TypeCell({ e }: { e: EventRow }) {
  const unassigned = isUnassignedRepay(e);
  const interestQ = e.interest_quantity !== null && isDecimalString(e.interest_quantity) && compareDecimal(e.interest_quantity, '0') > 0 ? e.interest_quantity : null;
  const result = e.repay_realized_result_incl_interest_usd ?? e.repay_realized_result_usd;
  return (
    <span className="stack" style={{ gap: 2 }}>
      <span>
        {eventTypeText(e)}
        {unassigned ? (
          <span className="pill warn" style={{ marginLeft: 6 }} data-testid="unassigned-badge" title={`Ebből ${formatDecimal(e.repay_unassigned_quantity ?? '0')} ${e.asset} egyik hitelhez sincs kötve; a Hitelek oldalon rendelheted hozzá.`}>
            Hozzárendelésre vár
          </span>
        ) : null}
      </span>
      {e.type === 'repay' && !e.voided && (interestQ !== null || e.repay_links.length > 0) ? (
        <span className="muted small" data-testid="repay-summary">
          {interestQ !== null ? <>tőke {formatDecimal(subDecimal(e.quantity, interestQ))} · kamat {formatDecimal(interestQ)} {e.asset}</> : null}
          {interestQ !== null && e.repay_links.length > 0 ? ' · ' : null}
          {e.repay_links.length > 0 ? <>{e.repay_links.length} hitelhez kötve · realizált{' '}
            {result === null ? <Dash reason={e.repay_realized_result_reason} /> : <span className={`mono ${resultClass(result)}`}>{formatDecimal(result, { dp: 2, sign: true })} USD</span>}</> : null}
          {e.interest_trueup_quantity !== null ? <> · kiigazítás {formatDecimal(e.interest_trueup_quantity, { sign: true })} {e.asset}</> : null}
        </span>
      ) : null}
    </span>
  );
}

interface Selection {
  /** The selected events by id (only events that can carry an allocation can be selected). */
  picked: ReadonlyMap<string, EventRow>;
  pageRows: EventRow[];
  toggle: (e: EventRow) => void;
  togglePage: () => void;
}

function columns(onEdit: (e: EventRow) => void, onVoid: (e: EventRow) => void, onAssign: (e: EventRow) => void, sel: Selection): Column<EventRow>[] {
  const key = (k: EventSortKey) => k;
  const selectable = sel.pageRows.filter(canAssignDeal);
  const allOn = selectable.length > 0 && selectable.every((e) => sel.picked.has(e.id));
  return [
    {
      key: 'sel',
      label: <input type="checkbox" aria-label="Az oldal összes hozzárendelhető eseményének kijelölése" checked={allOn} disabled={selectable.length === 0} onChange={sel.togglePage} />,
      render: (e) =>
        canAssignDeal(e) ? (
          <input type="checkbox" aria-label={`Kijelölés: ${formatUtc(e.occurred_at)}`} checked={sel.picked.has(e.id)} onChange={() => sel.toggle(e)} />
        ) : null,
    },
    { key: key('occurred_at'), label: 'Időpont (UTC)', sortValue: (e) => e.occurred_at, render: (e) => <span className="mono">{formatUtc(e.occurred_at)}</span> },
    { key: key('type'), label: 'Típus', sortValue: (e) => e.type, render: (e) => <TypeCell e={e} /> },
    { key: key('source'), label: 'Forrás', help: 'source', ariaLabel: 'forrás', sortValue: (e) => e.source, render: (e) => <SourcePills e={e} /> },
    { key: key('location'), label: 'Helyszín', sortValue: (e) => locationText(e), render: (e) => locationText(e) },
    { key: key('asset'), label: 'Coin', sortValue: (e) => e.asset, render: (e) => e.asset },
    { key: key('display_quantity'), label: 'Mennyiség', numeric: true, help: 'eventQuantity', ariaLabel: 'mennyiség', sortValue: (e) => e.display_quantity, render: quantityCell },
    { key: key('price'), label: 'Ár', numeric: true, help: 'price', ariaLabel: 'ár', sortValue: (e) => e.price, render: (e) => <span className="mono"><PriceCell e={e} /></span> },
    {
      key: key('fee_quantity'), label: 'Díj', numeric: true, sortValue: (e) => e.fee_quantity,
      render: (e) => (e.fee_quantity === null ? <span className="muted">—</span> : <span className="mono">{formatDecimal(e.fee_quantity)} {e.fee_asset ?? ''}</span>),
    },
    {
      key: key('deal'), label: 'Ügylet', sortValue: (e) => e.allocations[0]?.deal_name ?? (e.allocations[0] ? `#${e.allocations[0].deal_id}` : null),
      render: (e) =>
        e.allocations.length ? (
          <>
            {e.allocations.map((a, i) => (
              <span key={a.deal_id}>
                {i > 0 ? ', ' : ''}
                <Link to={`/deals/${a.deal_id}`} title={a.deal_name ?? undefined}>#{a.deal_id}</Link>
              </span>
            ))}
          </>
        ) : (
          <span className="muted">—</span>
        ),
    },
    {
      key: key('note'), label: 'Megjegyzés', wrap: true, sortValue: (e) => e.note,
      render: (e) => (
        <>
          {e.note ?? ''}
          {e.tags.map((t) => <span key={t} className="pill" style={{ marginLeft: 6 }}>{t}</span>)}
        </>
      ),
    },
    {
      key: 'act', label: 'Művelet',
      render: (e) =>
        e.voided ? (
          <span className="pill">Visszavonva</span>
        ) : (
          <span className="row-wrap">
            <button type="button" className="btn small" onClick={() => onEdit(e)} aria-label={`Megjegyzés szerkesztése: ${formatUtc(e.occurred_at)}`}>Megjegyzés</button>
            {canAssignDeal(e) ? <button type="button" className="btn small" onClick={() => onAssign(e)} aria-label={`Ügylethez rendelés: ${formatUtc(e.occurred_at)}`}>Ügylethez rendelés…</button> : null}
            <button type="button" className="btn small" onClick={() => onVoid(e)} aria-label={`Esemény visszavonása: ${formatUtc(e.occurred_at)}`}>Visszavonás</button>
          </span>
        ),
    },
  ];
}

function AnnotationDialog({ event, onClose, onSaved }: { event: EventRow; onClose: () => void; onSaved: () => void }) {
  const [note, setNote] = useState(event.note ?? '');
  const [tags, setTags] = useState(event.tags.join(', '));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await patchAnnotation(event.id, { note, tags: tags.split(',').map((t) => t.trim()).filter(Boolean) });
      onSaved();
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
      setBusy(false);
    }
  };
  return (
    <Dialog title="Megjegyzés és címkék" onClose={onClose}>
      <div className="muted small">{eventTypeText(event)} · {formatUtc(event.occurred_at)} UTC</div>
      <div className="field">
        <label htmlFor="ev-note">Megjegyzés</label>
        <textarea id="ev-note" className="input" maxLength={5000} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="ev-tags">Címkék (vesszővel elválasztva)</label>
        <input id="ev-tags" className="input" value={tags} onChange={(e) => setTags(e.target.value)} />
      </div>
      {err ? <ErrorNotice error={err} /> : null}
      <div className="row-wrap">
        <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>Mentés</button>
        <button type="button" className="btn" onClick={onClose}>Mégse</button>
      </div>
    </Dialog>
  );
}

function VoidDialog({ event, onClose, onDone }: { event: EventRow; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);
  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      await postVoidEvent(event.id, reason.trim());
      onDone();
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
      setBusy(false);
    }
  };
  return (
    <Dialog title="Esemény visszavonása" onClose={onClose}>
      <div>
        <b>{eventTypeText(event)}</b> · {formatUtc(event.occurred_at)} UTC · <span className="mono">{event.display_quantity} {event.asset}</span>
      </div>
      <p style={{ margin: 0 }}>
        Az esemény kikerül a főkönyvből (nem számít bele az egyenlegekbe és az ügyletekbe), <b>de az audit-naplóban megmarad</b>: a „Visszavontak is” szűrővel később is megnézheted.
      </p>
      <div className="field">
        <label htmlFor="ev-void-reason">Indok (nem kötelező)</label>
        <input id="ev-void-reason" className="input" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
      </div>
      {err ? <ErrorNotice error={err} /> : null}
      <div className="row-wrap">
        <button type="button" className="btn primary" disabled={busy} onClick={() => void run()}>Igen, visszavonom</button>
        <button type="button" className="btn" onClick={onClose}>Mégse</button>
      </div>
    </Dialog>
  );
}

function TypePicker({ filter, counts, onChange }: { filter: EventsFilter; counts: EventsPage['facets']; onChange: (types: EventType[]) => void }) {
  const n = filter.types.length;
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    // The list is a popover: a click elsewhere or Escape closes it.
    const close = (e: Event) => {
      const d = ref.current;
      if (!d?.open) return;
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !d.contains(e.target as Node)) d.open = false;
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, []);
  return (
    <details className="msel" ref={ref}>
      <summary className="input" aria-label="Típus szűrő">
        {n === ALL_TYPES.length ? `Mind (${n})` : `${n} / ${ALL_TYPES.length} kiválasztva`}
      </summary>
      <div className="msel-panel" role="group" aria-label="Típus">
        <div className="row-wrap">
          <button type="button" className="btn small" onClick={() => onChange([...ALL_TYPES])}>Mind</button>
          <button type="button" className="btn small" onClick={() => onChange([])}>Egyik sem</button>
        </div>
        {ALL_TYPES.map((t) => (
          <label key={t} className="check">
            <input type="checkbox" checked={filter.types.includes(t)} onChange={() => onChange(toggle(filter.types, ALL_TYPES, t))} />
            <span>{TYPE_LABEL[t]}</span>
            {counts ? <span className="muted small mono">{counts.type[t] ?? 0}</span> : null}
          </label>
        ))}
      </div>
    </details>
  );
}

export function EventsPageView() {
  const { search, navigate } = useRouter();
  const [filter, setFilter] = useState<EventsFilter>(() => initialFilter(search));
  const [assetInput, setAssetInput] = useState(filter.asset);
  const [editing, setEditing] = useState<EventRow | null>(null);
  const [voiding, setVoiding] = useState<EventRow | null>(null);
  const [assigning, setAssigning] = useState<EventRow | null>(null);
  const [picked, setPicked] = useState<ReadonlyMap<string, EventRow>>(new Map());
  const [bulk, setBulk] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const serialized = filterToParams(filter).toString();

  // Persist and mirror into the URL (replace: a filter tweak is not a history entry).
  useEffect(() => {
    storeFilter(filter);
    const want = serialized ? `?${serialized}` : '';
    if (window.location.search !== want) navigate(`/events${want}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
  // Back/forward or a pasted bookmark while the page is open.
  useEffect(() => {
    const parsed = paramsToFilter(new URLSearchParams(search));
    if (parsed && filterToParams(parsed).toString() !== serialized) {
      setFilter(parsed);
      setAssetInput(parsed.asset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  // The coin box applies after a short pause.
  useEffect(() => {
    const v = assetInput.trim().toUpperCase();
    if (v === filter.asset) return;
    const t = setTimeout(() => setFilter((f) => ({ ...f, asset: v, page: 1 })), 350);
    return () => clearTimeout(t);
  }, [assetInput, filter.asset]);

  const change = useCallback((patch: Partial<EventsFilter>) => setFilter((f) => ({ ...f, ...patch, page: 1 })), []);
  const built = useMemo(() => filterToQuery(filter), [serialized]); // eslint-disable-line react-hooks/exhaustive-deps
  const skip = built.empty || built.periodError !== null;
  const events = useApi((signal) => (skip ? Promise.resolve(EMPTY_PAGE) : getEventsPage(built.query, signal)), [JSON.stringify(built.query), skip]);
  const locations = useApi((s) => getLocationItems(s));
  const deals = useApi((s) => getDeals(s));

  const page = events.data ?? EMPTY_PAGE;
  const pages = Math.max(1, Math.ceil(page.total / filter.size));
  useEffect(() => {
    // The last row of the last page was voided / the list shrank: step back to a page that exists.
    if (events.data && events.data.items.length === 0 && events.data.total > 0 && filter.page > pages) setFilter((f) => ({ ...f, page: pages }));
  }, [events.data, filter.page, pages]);

  // The selection belongs to the view it was made in: a changed filter, sort or page starts it over.
  useEffect(() => setPicked(new Map()), [serialized]);
  const sel = useMemo<Selection>(
    () => ({
      picked,
      pageRows: page.items,
      toggle: (e) => setPicked((p) => { const n = new Map(p); if (n.has(e.id)) n.delete(e.id); else n.set(e.id, e); return n; }),
      togglePage: () => setPicked((p) => {
        const rows = page.items.filter(canAssignDeal);
        const n = new Map(p);
        if (rows.every((e) => n.has(e.id))) rows.forEach((e) => n.delete(e.id));
        else rows.forEach((e) => n.set(e.id, e));
        return n;
      }),
    }),
    [picked, page.items],
  );
  const cols = useMemo(() => columns(setEditing, setVoiding, setAssigning, sel), [sel]);
  const afterWrite = (msg: string | null) => {
    setEditing(null);
    setVoiding(null);
    setAssigning(null);
    setBulk(false);
    setPicked(new Map());
    setFlash(msg);
    events.reload();
  };
  const first = page.total === 0 ? 0 : (filter.page - 1) * filter.size + 1;
  const last = Math.min(filter.page * filter.size, page.total);
  const facets = page.facets;

  return (
    <main className="page">
      <div className="page-head">
        <div className="titles">
          <h1>Események</h1>
          <div className="muted">Minden naplózott esemény egy helyen: kézi, Binance és számolt kamat · szűrhető, rendezhető · minden időpont UTC</div>
        </div>
        <Link className="btn" to="/entries/new">Kézi bevitel</Link>
      </div>

      <section className="card" aria-label="Szűrők" style={{ gap: 16 }}>
        <div className="row-wrap" style={{ alignItems: 'flex-end', gap: '16px 24px' }}>
          <div className="field">
            <HelpLabel helpKey="eventsSource" label="forrás" className="flabel">Forrás</HelpLabel>
            <div className="row-wrap" role="group" aria-label="Forrás">
              {ALL_SOURCES.map((s) => {
                const on = filter.sources.includes(s);
                return (
                  <button key={s} type="button" className={`chip${on ? ' on' : ''}`} aria-pressed={on} onClick={() => change({ sources: toggle(filter.sources, ALL_SOURCES, s) })}>
                    {SOURCE_CHIP[s]}
                    {facets ? <span className="chip-n mono" data-testid={`facet-source-${s}`}>{facets.source[s] ?? 0}</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="field">
            <span className="flabel">Típus</span>
            <TypePicker filter={filter} counts={facets} onChange={(types) => change({ types })} />
          </div>
          <div className="field">
            <label htmlFor="ev-loc">Helyszín</label>
            <select id="ev-loc" className="input" value={filter.locationId} onChange={(e) => change({ locationId: e.target.value })}>
              <option value="">Mind</option>
              {(locations.data ?? []).filter((l) => !l.archived).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label htmlFor="ev-asset">Coin</label>
            <input id="ev-asset" className="input" list="ev-assets" placeholder="pl. BTC" maxLength={20} style={{ width: 120 }} value={assetInput} onChange={(e) => setAssetInput(e.target.value)} />
            <datalist id="ev-assets">{[...new Set(page.items.map((e) => e.asset))].map((a) => <option key={a} value={a} />)}</datalist>
          </div>
          <div className="field">
            <label htmlFor="ev-deal">Ügylet</label>
            <select id="ev-deal" className="input" value={filter.deal} onChange={(e) => change({ deal: e.target.value })}>
              <option value="">Mind</option>
              <option value="none">Ügylet nélkül</option>
              {(deals.data ?? []).map((d) => <option key={d.id} value={String(d.id)}>#{d.id} {d.name}</option>)}
            </select>
          </div>
        </div>
        <div className="row-wrap" style={{ alignItems: 'flex-end', gap: '16px 24px' }}>
          <div className="field">
            <HelpLabel helpKey="eventsPeriod" label="időszak" className="flabel">Időszak (UTC)</HelpLabel>
            <div className="seg" role="group" aria-label="Időszak">
              {(Object.keys(PERIOD_LABEL) as Period[]).map((p) => (
                <button
                  key={p} type="button" className={filter.period === p ? 'on' : ''} aria-pressed={filter.period === p}
                  onClick={() => change(p === 'custom' && !filter.from ? { period: p, ...defaultCustomRange(new Date()) } : { period: p })}
                >
                  {PERIOD_LABEL[p]}
                </button>
              ))}
            </div>
          </div>
          {filter.period === 'custom' ? (
            <>
              <div className="field">
                <label htmlFor="ev-from">-tól (UTC, tartalmazza)</label>
                <input id="ev-from" className="input mono" placeholder="ÉÉÉÉ-HH-NN ÓÓ:PP" value={filter.from} onChange={(e) => change({ from: e.target.value })} style={{ width: 170 }} />
              </div>
              <div className="field">
                <label htmlFor="ev-to">-ig (UTC, nem tartalmazza)</label>
                <input id="ev-to" className="input mono" placeholder="ÉÉÉÉ-HH-NN ÓÓ:PP" value={filter.to} onChange={(e) => change({ to: e.target.value })} style={{ width: 170 }} />
              </div>
            </>
          ) : null}
          <div className="field">
            <HelpLabel helpKey="unassignedRepay" label="hozzárendelésre váró visszafizetés" className="flabel">Visszafizetések</HelpLabel>
            <button
              type="button" className={`chip${filter.repayUnassigned ? ' on' : ''}`} aria-pressed={filter.repayUnassigned} data-testid="events-unassigned-chip"
              onClick={() => change({ repayUnassigned: !filter.repayUnassigned })}
            >
              Hozzárendelésre vár
            </button>
          </div>
          <div className="field">
            <label className="check" htmlFor="ev-voided">
              <input id="ev-voided" type="checkbox" checked={filter.includeVoided} onChange={(e) => change({ includeVoided: e.target.checked })} />
              <span>Visszavontak is</span>
            </label>
          </div>
          <button type="button" className="btn" disabled={isDefaultFilter(filter)} onClick={() => { const c = clearFilters(filter); setFilter(c); setAssetInput(''); }}>
            Szűrők törlése
          </button>
        </div>
        {built.periodError ? <Notice kind="warn" title="Az időszak nem használható">{built.periodError}</Notice> : null}
        {filter.sources.length === 0 || filter.types.length === 0 ? <Notice kind="info" title="Nincs kiválasztott forrás vagy típus">Kapcsolj be legalább egyet, különben nincs mit mutatni.</Notice> : null}
      </section>

      {flash ? <Notice kind="ok" title={flash} /> : null}
      {events.error ? <ErrorNotice error={events.error} onRetry={events.reload} /> : null}

      <section className="card tight" aria-label="Események">
        <CardHead title="Események" hint={<span data-testid="events-total">{page.total} találat{events.loading && events.data ? ' · frissítés…' : ''}</span>} />
        {picked.size > 0 ? (
          <div className="row-wrap" style={{ alignItems: 'center' }} data-testid="events-bulkbar">
            <span className="small" data-testid="events-picked">{picked.size} kijelölve</span>
            <button type="button" className="btn small primary" onClick={() => setBulk(true)}>Kijelöltek ügylethez rendelése…</button>
            <button type="button" className="btn small" onClick={() => setPicked(new Map())}>Kijelölés törlése</button>
          </div>
        ) : null}
        {events.loading && !events.data ? (
          <Loading />
        ) : (
          <Table
            columns={cols}
            rows={page.items}
            rowKey={(e) => e.id}
            dense
            sort={{ key: filter.sort, dir: filter.order }}
            onSortChange={(s) => change({ sort: s.key as EventSortKey, order: s.dir })}
            rowClass={(e) => (e.voided ? 'row-voided' : undefined)}
            label="Események"
            empty="Nincs a szűrőnek megfelelő esemény."
          />
        )}
        <div className="pager" role="navigation" aria-label="Lapozás">
          <span data-testid="events-range">{first}–{last} / {page.total}</span>
          <label className="row-wrap" style={{ alignItems: 'center' }}>
            <span className="muted small">Sor / oldal</span>
            <select className="input" style={{ minHeight: 36 }} aria-label="Sor / oldal" value={filter.size} onChange={(e) => change({ size: Number(e.target.value) as PageSize })}>
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button type="button" className="btn small" disabled={filter.page <= 1} onClick={() => setFilter((f) => ({ ...f, page: f.page - 1 }))}>← Előző</button>
          <span className="small" data-testid="events-page">{filter.page}. / {pages}. oldal</span>
          <button type="button" className="btn small" disabled={filter.page >= pages} onClick={() => setFilter((f) => ({ ...f, page: f.page + 1 }))}>Következő →</button>
        </div>
      </section>

      {editing ? <AnnotationDialog event={editing} onClose={() => setEditing(null)} onSaved={() => afterWrite('A megjegyzés elmentve.')} /> : null}
      {assigning ? <AssignDealDialog event={assigning} onClose={() => setAssigning(null)} onSaved={() => afterWrite('Az ügylet-hozzárendelés elmentve.')} /> : null}
      {bulk ? <BulkAssignDialog events={[...picked.values()]} onClose={() => setBulk(false)} onDone={() => afterWrite('A kijelölt események hozzárendelése lefutott.')} /> : null}
      {voiding ? <VoidDialog event={voiding} onClose={() => setVoiding(null)} onDone={() => afterWrite('Az esemény visszavonva; az audit-naplóban megmarad.')} /> : null}
    </main>
  );
}
